// Configuração da SPA (Parte 14.8): em dev vem de .env (VITE_*); em stage/prod o bootstrap lê GET /config.json (host
// Giyus.Web, app settings) e sobrepõe as chaves de runtime com `resolveAppEnv` — o bundle é o mesmo em todo ambiente.
import type { RuntimeConfig } from '@giyus/api-client';
import { z } from 'zod';

export const AUTH_MODES = ['oidc', 'fake'] as const;
export type AuthMode = (typeof AUTH_MODES)[number];

export const FAKE_ME_SCENARIOS = [
  'none',
  'single',
  'multi',
  'network-only',
  'network+multi',
  // GIY-056: as duas chaves da publicação da matriz (QA primeira chave; DPO segunda, outro papel) e o investigador
  // principal de um estudo do centro-âncora (break-glass; papel de estudo).
  'network-quality',
  'network-dpo',
  'tenant-investigator',
  // GIY-073 parte B (OPEN-078): o investigador principal com um acesso emergencial (break-glass) em vigor — a faixa permanente do shell.
  'tenant-break-glass',
  // GIY-075 (delegation log): a enfermeira delegada (contexto só pela delegação; uma delegação ativa e uma pendente de aceite) e a coordenadora
  // líder (prepara e emenda, não assina nem lê o log).
  'tenant-delegate',
  'tenant-lead-coordinator',
  // GIY-067 (Pessoas): o RH da rede (`PEOPLE_ADMIN`, 1.ª chave da Matriz e da exceção de transição) e o Jurídico (`LEGAL`, 2.ª chave).
  'network-people',
  'network-legal',
  // GIY-067: a administradora do centro piloto SEM acesso de rede (RH do centro: lê e gere as pessoas do próprio centro).
  'tenant-admin',
  // Super-Admin Corporativo da Holding GIYUS (syllas@giyus.com.br) com visão unificada de Rede + Centros.
  'syllas-admin',
] as const;
export type FakeMeScenario = (typeof FAKE_ME_SCENARIOS)[number];

export const ENVIRONMENTS = ['dev', 'stage', 'prod'] as const;
export type EnvironmentName = (typeof ENVIRONMENTS)[number];

const envSchema = z.object({
  VITE_API_BASE_URL: z.url().default('http://localhost:5000'),
  VITE_OIDC_AUTHORITY: z.url().default('http://localhost:5000'),
  VITE_OIDC_CLIENT_ID: z.string().min(1).default('giyus-web'),
  VITE_AUTH_MODE: z.enum(AUTH_MODES).default('oidc'),
  VITE_FAKE_ME_SCENARIO: z.enum(FAKE_ME_SCENARIOS).default('single'),
  VITE_ENVIRONMENT: z.enum(ENVIRONMENTS).default('dev'),
});

const runtimeSchema = z.object({
  apiBaseUrl: z.url().optional(),
  oidcAuthority: z.url().optional(),
  oidcClientId: z.string().min(1).optional(),
  environment: z.enum(ENVIRONMENTS).optional(),
});

export interface AppEnv {
  readonly apiBaseUrl: string;
  readonly oidcAuthority: string;
  readonly oidcClientId: string;
  readonly authMode: AuthMode;
  readonly fakeMeScenario: FakeMeScenario;
  readonly environment: EnvironmentName;
}

// Parte 0.6 (sem modo demo): `fake` só existe em `dev`. Nenhum cenário — nem `syllas-admin` — abre exceção fora de dev:
// um bundle de stage/prod construído em modo fake falha aqui no bootstrap, antes de renderizar qualquer tela.
function assertNoDemoMode(env: AppEnv): void {
  if (env.environment !== 'dev' && env.authMode === 'fake') {
    throw new Error('VITE_AUTH_MODE=fake is only allowed in dev (Parte 0.6: no demo mode).');
  }
}

/** Valida e normaliza as variáveis; valores ausentes recebem padrão, valores inválidos falham cedo. */
export function parseEnv(raw: Readonly<Record<string, unknown>>): AppEnv {
  const cleaned = Object.fromEntries(
    Object.entries(raw).filter(([key, value]) => key.startsWith('VITE_') && value !== ''),
  );
  const parsed = envSchema.parse(cleaned);
  const env: AppEnv = {
    apiBaseUrl: parsed.VITE_API_BASE_URL,
    oidcAuthority: parsed.VITE_OIDC_AUTHORITY,
    oidcClientId: parsed.VITE_OIDC_CLIENT_ID,
    authMode: parsed.VITE_AUTH_MODE,
    fakeMeScenario: parsed.VITE_FAKE_ME_SCENARIO,
    environment: parsed.VITE_ENVIRONMENT,
  };
  assertNoDemoMode(env);
  return env;
}

/**
 * Sobrepõe as chaves de runtime de `/config.json` (URL da API, autoridade OIDC, clientId, ambiente) ao `.env`;
 * `null` (host sem config.json) mantém o `.env`. Valores presentes mas inválidos falham cedo, como em `parseEnv`.
 */
export function resolveAppEnv(base: AppEnv, runtime: Partial<RuntimeConfig> | null): AppEnv {
  if (!runtime) {
    return base;
  }
  const parsed = runtimeSchema.parse(runtime);
  const resolved: AppEnv = {
    ...base,
    apiBaseUrl: parsed.apiBaseUrl ?? base.apiBaseUrl,
    oidcAuthority: parsed.oidcAuthority ?? base.oidcAuthority,
    oidcClientId: parsed.oidcClientId ?? base.oidcClientId,
    environment: parsed.environment ?? base.environment,
  };
  assertNoDemoMode(resolved);
  return resolved;
}

/** Configuração de build/.env (sem /config.json); o bootstrap resolve a efetiva e a entrega pelo `AppEnvContext`. */
export const env: AppEnv = parseEnv(import.meta.env as unknown as Record<string, unknown>);

export const APP_VERSION: string = __APP_VERSION__;
